import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { AppHeader } from "@/components/layout/AppHeader";
import { useAuth } from "@clerk/clerk-react";
import { Flame, MessageCircle, User, ChevronRight, Sparkles, X, Loader2, Video } from "lucide-react";
import { motion } from "framer-motion";
import { useRef } from "react";
import { HallyChat } from "@/components/ai-chat";

interface ActiveMatch {
    id: string;
    compatibilityScore: number;
    otherUser: {
        id: string;
        displayName: string;
        photoUrl: string | null;
        role: string;
    };
}

interface FeedMatch {
    userId: string;
    compatibilityScore: number;
    profile: {
        displayName: string;
        photos: string[];
    };
    explanation: {
        reasons: string[];
    };
}

interface MatchFeedResponse {
    matches: FeedMatch[];
}

export function Dashboard() {
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const carouselRef = useRef<HTMLDivElement>(null);

    const { data, isLoading } = useQuery<{ matches: ActiveMatch[] }>({
        queryKey: ["activeMatches"],
        queryFn: async () => {
            const token = await getToken();
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/api/matches`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch matches");
            return res.json();
        },
    });

    const { data: feedData, isLoading: isFeedLoading } = useQuery<MatchFeedResponse>({
        queryKey: ["matchFeed", "preview"],
        queryFn: async () => {
            const token = await getToken();
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/api/matches/feed?limit=10`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch feed");
            return res.json();
        },
    });

    const queryClient = useQueryClient();

    const closeConnectionMutation = useMutation({
        mutationFn: async (matchId: string) => {
            const token = await getToken();
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/api/matches/${matchId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ status: 'closed' }),
            });
            if (!res.ok) throw new Error("Failed to close connection");
            return res.json();
        },
        onSuccess: (_data, matchId) => {
            queryClient.setQueryData(["activeMatches"], (old: { matches: ActiveMatch[] } | undefined) => {
                if (!old) return old;
                return {
                    matches: old.matches.filter(m => m.id !== matchId)
                };
            });
        },
    });

    const handleCloseConnection = (e: React.MouseEvent, matchId: string) => {
        e.preventDefault();
        e.stopPropagation();
        closeConnectionMutation.mutate(matchId);
    };

    return (
        <div className="min-h-screen bg-brand-background text-brand-text font-sans">
            <AppHeader />

            <main className="p-4 pt-24 max-w-6xl mx-auto space-y-8 animate-fade-in">
                <header>
                    <h1 className="text-4xl font-heading font-bold bg-clip-text text-transparent bg-brand-gradient mb-2">
                        Report Card
                    </h1>
                    <p className="text-brand-muted text-lg">
                        Manage your relations, interests, and discover matches.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Discover People - Carousel Section (2x2) */}
                    <div className="md:col-span-2 md:row-span-2 rounded-3xl bg-brand-surface border border-brand-border p-6 min-h-[400px] flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-xl bg-brand-primary/10 text-brand-primary">
                                    <Flame size={24} />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-heading font-bold text-white">Discover People</h2>
                                    <p className="text-sm text-brand-muted">
                                        {isFeedLoading ? "Finding matches..." :
                                         feedData?.matches?.length ? `${feedData.matches.length} compatible ${feedData.matches.length === 1 ? 'match' : 'matches'}` :
                                         "No matches yet"}
                                    </p>
                                </div>
                            </div>
                            <Link
                                to="/feed"
                                className="flex items-center text-brand-primary text-sm font-medium hover:underline"
                            >
                                View All <ChevronRight size={16} className="ml-1" />
                            </Link>
                        </div>

                        {/* Carousel */}
                        <div className="flex-1 overflow-hidden relative">
                            {isFeedLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader2 className="animate-spin text-brand-primary" size={32} />
                                </div>
                            ) : feedData?.matches && feedData.matches.length > 0 ? (
                                <motion.div
                                    ref={carouselRef}
                                    className="flex gap-4 cursor-grab active:cursor-grabbing h-full items-stretch"
                                    drag="x"
                                    dragConstraints={{ right: 0, left: -(feedData.matches.length * 220 - 400) }}
                                    whileTap={{ cursor: "grabbing" }}
                                >
                                    {feedData.matches.map((match) => (
                                        <motion.div
                                            key={match.userId}
                                            className="flex-shrink-0 w-52 rounded-2xl overflow-hidden bg-brand-background border border-brand-border hover:border-brand-primary/50 transition-all cursor-pointer group"
                                            whileHover={{ scale: 1.02, zIndex: 10 }}
                                            transition={{ duration: 0.2 }}
                                            onClick={() => navigate(`/feed?profile=${match.userId}`)}
                                        >
                                            {/* Photo */}
                                            <div className="relative h-60 overflow-hidden">
                                                {match.profile.photos[0] ? (
                                                    <img
                                                        src={match.profile.photos[0]}
                                                        alt={match.profile.displayName}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        draggable={false}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-brand-highlight flex items-center justify-center">
                                                        <User className="text-brand-muted" size={48} />
                                                    </div>
                                                )}
                                                {/* Compatibility Badge */}
                                                <div className="absolute top-2 right-2 bg-brand-background/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center space-x-1">
                                                    <Sparkles size={12} className="text-brand-secondary" />
                                                    <span className="text-xs font-medium text-brand-secondary">
                                                        {match.compatibilityScore || 95}%
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Info */}
                                            <div className="p-3">
                                                <h3 className="font-bold text-white text-lg truncate">{match.profile.displayName}</h3>
                                                {match.explanation?.reasons?.[0] && (
                                                    <p className="text-xs text-brand-muted line-clamp-2 mt-1">
                                                        {match.explanation.reasons[0]}
                                                    </p>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-center">
                                    <div className="p-4 rounded-2xl bg-brand-primary/10 text-brand-primary mb-4">
                                        <Flame size={48} />
                                    </div>
                                    <p className="text-xl text-brand-muted mb-2">No matches yet</p>
                                    <p className="text-sm text-brand-muted mb-4">Complete your profile to start matching</p>
                                    <Link
                                        to="/profile/preview"
                                        className="text-brand-primary font-medium hover:underline flex items-center"
                                    >
                                        Complete Profile <ChevronRight size={16} className="ml-1" />
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Connections - Side Panel (1x2) */}
                    <div className="md:col-span-1 md:row-span-2 rounded-3xl bg-brand-surface border border-brand-border p-6 relative overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between mb-6 flex-shrink-0">
                            <h2 className="text-xl font-bold text-white flex items-center">
                                <MessageCircle className="mr-3 text-brand-secondary" size={24} />
                                Connections
                            </h2>
                            <span className="bg-brand-highlight px-3 py-1 rounded-full text-xs text-brand-muted">
                                {data?.matches.length || 0}
                            </span>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-32 text-brand-muted animate-pulse">
                                    Loading...
                                </div>
                            ) : data?.matches && data.matches.length > 0 ? (
                                <div className="space-y-3">
                                    {data.matches.map((match) => (
                                        <Link
                                            key={match.id}
                                            to={`/chat/${match.id}`}
                                            className="flex items-center p-3 rounded-xl bg-brand-background/50 border border-brand-border hover:border-brand-secondary/50 transition-colors cursor-pointer group"
                                        >
                                            <div className="w-10 h-10 rounded-full bg-brand-surface overflow-hidden border border-brand-border flex-shrink-0">
                                                {match.otherUser.photoUrl ? (
                                                    <img src={match.otherUser.photoUrl} alt={match.otherUser.displayName} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-brand-highlight text-brand-muted">
                                                        <User size={16} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="ml-3 flex-1 min-w-0">
                                                <h3 className="font-semibold text-white truncate text-sm">{match.otherUser.displayName}</h3>
                                                <div className="flex items-center text-[10px] text-brand-secondary">
                                                    <Sparkles size={10} className="mr-1" />
                                                    {match.compatibilityScore}%
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => handleCloseConnection(e, match.id)}
                                                className="p-1.5 rounded-full hover:bg-brand-highlight text-brand-muted hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                                title="Close Connection"
                                            >
                                                <X size={16} />
                                            </button>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-center p-4 rounded-2xl bg-brand-background/30 border border-dashed border-brand-border/50">
                                    <User className="w-8 h-8 text-brand-muted/30 mb-3" />
                                    <p className="text-sm text-brand-muted mb-2">No active connections.</p>
                                    <Link to="/feed" className="text-xs text-brand-primary hover:underline">
                                        Start matching!
                                    </Link>
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Bottom Row: Hally (2-col) + Stacked Cards (1-col) */}
                    <div className="md:col-span-2">
                        <HallyChat compact />
                    </div>

                    {/* Stacked: My Profile + Video Dates */}
                    <div className="flex flex-col gap-6">
                        {/* My Profile Card */}
                        <Link
                            to="/profile/preview"
                            className="group relative overflow-hidden rounded-3xl bg-brand-surface border border-brand-border p-5 hover:border-brand-accent/50 transition-all duration-300 flex-1"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-brand-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                            <div className="relative z-10 flex items-center gap-4">
                                <div className="p-2.5 rounded-xl bg-brand-accent/10 text-brand-accent group-hover:bg-brand-accent/20 transition-colors">
                                    <User size={20} />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-base font-bold text-white">My Profile</h2>
                                    <p className="text-xs text-brand-muted">Edit how others see you</p>
                                </div>
                                <ChevronRight size={16} className="text-brand-muted group-hover:text-white transition-colors" />
                            </div>
                        </Link>

                        {/* Video Dates Placeholder */}
                        <div className="rounded-3xl bg-brand-surface border border-brand-border p-5 flex-1">
                            <div className="flex items-center gap-4">
                                <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400">
                                    <Video size={20} />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-base font-bold text-white">Video Dates</h2>
                                    <p className="text-xs text-brand-muted">Schedule virtual dates</p>
                                </div>
                                <span className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full text-[10px] font-medium">
                                    Soon
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </main>
        </div>
    );
}
